/**
 * BrowserWindowManager.tsx
 *
 * Opens REAL browser windows (window.open) that can be dragged around the desktop.
 * Each window is a separate browser window with its own React app instance.
 *
 * Architecture:
 * - Parent window stores state and communicates via postMessage
 * - Child windows render standalone React components
 * - Cross-window communication for real-time updates
 *
 * 2026-01-21: NexEmerge Project - True Multi-Window Experience
 */

import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface BrowserWindowOptions {
  width?: number;
  height?: number;
  left?: number;
  top?: number;
}

export interface ReadingTheatreData {
  sessionId?: string; // For independent polling when parent disconnects
  aiName: string;
  documentTitle: string;
  currentParagraph: number;
  totalParagraphs: number;
  progress: number;
  status: 'Queued' | 'Reading' | 'Completed' | 'Failed' | 'Paused';
  currentParagraphContent?: string;
  currentThought?: string;
}

export interface ConsultWindowData {
  personalId: string;
  nexProfName: string;
  domainExpertise: string;
}

// =============================================================================
// CONTEXT
// =============================================================================

interface BrowserWindowContextType {
  openReadingTheatre: (data: ReadingTheatreData, options?: BrowserWindowOptions) => Window | null;
  openConsultWindow: (data: ConsultWindowData, options?: BrowserWindowOptions) => Window | null;
  updateReadingTheatre: (data: Partial<ReadingTheatreData>, sessionId?: string) => void;
  closeAllWindows: () => void;
}

const BrowserWindowContext = createContext<BrowserWindowContextType | null>(null);

export const useBrowserWindows = () => {
  const context = useContext(BrowserWindowContext);
  if (!context) {
    throw new Error('useBrowserWindows must be used within BrowserWindowProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface BrowserWindowProviderProps {
  children: React.ReactNode;
}

export const BrowserWindowProvider: React.FC<BrowserWindowProviderProps> = ({ children }) => {
  const openWindowsRef = useRef<Map<string, Window>>(new Map());

  // Clean up windows on unmount
  useEffect(() => {
    return () => {
      openWindowsRef.current.forEach((win) => {
        if (win && !win.closed) {
          win.close();
        }
      });
    };
  }, []);

  // Listen for messages from child windows
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our own windows
      if (event.origin !== window.location.origin) return;

      const { type, windowId } = event.data || {};

      if (type === 'WINDOW_CLOSED') {
        openWindowsRef.current.delete(windowId);
      }

      if (type === 'CONSULT_MESSAGE') {
        // Handle consult messages - could dispatch to API here
        console.log('Received consult message:', event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const calculateWindowPosition = useCallback((width: number, height: number) => {
    // Center on screen, offset slightly for each new window
    const offsetMultiplier = openWindowsRef.current.size;
    const offset = offsetMultiplier * 30;

    const left = Math.max(0, (window.screen.width - width) / 2 + offset);
    const top = Math.max(0, (window.screen.height - height) / 2 + offset);

    return { left, top };
  }, []);

  const openReadingTheatre = useCallback((data: ReadingTheatreData, options?: BrowserWindowOptions) => {
    const width = options?.width || 1200;
    const height = options?.height || 1700;
    const { left, top } = options?.left !== undefined
      ? { left: options.left, top: options.top || 100 }
      : calculateWindowPosition(width, height);

    // Include sessionId in window ID for per-session targeting.
    // Previously used Date.now() only, causing updateReadingTheatre to broadcast
    // to ALL theatre windows — when two AIs read simultaneously, one took over the other.
    const windowId = data.sessionId
      ? `reading-theatre-${data.sessionId}`
      : `reading-theatre-${Date.now()}`;

    // Encode data for URL - include sessionId for independent polling
    const params = new URLSearchParams({
      windowId,
      data: btoa(encodeURIComponent(JSON.stringify(data))),
      ...(data.sessionId ? { sessionId: data.sessionId } : {}),
    });

    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=no',
    ].join(',');

    const newWindow = window.open(
      `/nexprof/reading-theatre?${params.toString()}`,
      windowId,
      features
    );

    if (newWindow) {
      openWindowsRef.current.set(windowId, newWindow);

      // Monitor for window close
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          openWindowsRef.current.delete(windowId);
          clearInterval(checkClosed);
        }
      }, 500);
    }

    return newWindow;
  }, [calculateWindowPosition]);

  const openConsultWindow = useCallback((data: ConsultWindowData, options?: BrowserWindowOptions) => {
    const width = options?.width || 500;
    const height = options?.height || 700;
    const { left, top } = options?.left !== undefined
      ? { left: options.left, top: options.top || 100 }
      : calculateWindowPosition(width, height);

    const windowId = `consult-${data.personalId}-${Date.now()}`;

    // Encode data for URL
    const params = new URLSearchParams({
      windowId,
      data: btoa(encodeURIComponent(JSON.stringify(data))),
    });

    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=no',
    ].join(',');

    const newWindow = window.open(
      `/nexprof/consult?${params.toString()}`,
      windowId,
      features
    );

    if (newWindow) {
      openWindowsRef.current.set(windowId, newWindow);

      // Monitor for window close
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          openWindowsRef.current.delete(windowId);
          clearInterval(checkClosed);
        }
      }, 500);
    }

    return newWindow;
  }, [calculateWindowPosition]);

  const updateReadingTheatre = useCallback((data: Partial<ReadingTheatreData>, sessionId?: string) => {
    // Send update to reading theatre windows, scoped by sessionId when provided.
    // Previously broadcast to ALL reading-theatre-* windows, causing one session
    // to overwrite another when two AIs read simultaneously.
    const targetWindowId = sessionId ? `reading-theatre-${sessionId}` : null;

    openWindowsRef.current.forEach((win, id) => {
      if (id.startsWith('reading-theatre-') && !win.closed) {
        // If we have a target, only send to that specific window
        if (targetWindowId && id !== targetWindowId) return;
        win.postMessage({ type: 'UPDATE_READING', data, sessionId }, window.location.origin);
      }
    });
  }, []);

  const closeAllWindows = useCallback(() => {
    openWindowsRef.current.forEach((win) => {
      if (!win.closed) {
        win.close();
      }
    });
    openWindowsRef.current.clear();
  }, []);

  return (
    <BrowserWindowContext.Provider
      value={{
        openReadingTheatre,
        openConsultWindow,
        updateReadingTheatre,
        closeAllWindows,
      }}
    >
      {children}
    </BrowserWindowContext.Provider>
  );
};

export default BrowserWindowProvider;
